import { SerialsData, TableLicensesProps } from "types/module/License";

import Table from "components/ui/Table/Table";
import { Alert, Button, Checkbox, Tooltip, Flex } from "@mantine/core";
import { FaExclamationCircle, FaInfoCircle } from "react-icons/fa";
import { MSG } from "constants/msg";

const TableLicenses: React.FC<TableLicensesProps> = ({
	serialsData,
	isSerialsDataLoading,
	handleSelectLicense,
	selectedPools = [],
	onPoolSelectionChange,
	showCheckboxes = false,
}) => {
	const handleCheckboxChange = (uuid: string, checked: boolean) => {
		if (!onPoolSelectionChange) return;

		if (checked) {
			onPoolSelectionChange([...selectedPools, uuid]);
		} else {
			onPoolSelectionChange(selectedPools.filter(id => id !== uuid));
		}
	};

	const handleSelectAll = (checked: boolean) => {
		if (!onPoolSelectionChange) return;

		if (checked) {
			const allUuids = serialsData.filter(item => item.uuid).map(item => item.uuid as string);
			onPoolSelectionChange(allUuids);
		} else {
			onPoolSelectionChange([]);
		}
	};

	const allSelected = serialsData.length > 0 &&
		serialsData.filter(item => item.uuid).every(item => selectedPools.includes(item.uuid as string));

	const someSelected = selectedPools.length > 0;

	const columns = [
		...(showCheckboxes ? [{
			id: 'select',
			header: () => (
				<Checkbox
					checked={allSelected}
					indeterminate={someSelected && !allSelected}
					onChange={(event) => handleSelectAll(event.currentTarget.checked)}
					color="gray"
					size="sm"
				/>
			),
			cell: ({ row }: { row: { original: SerialsData } }) => (
				row.original.uuid ? (
					<Checkbox
						checked={selectedPools.includes(row.original.uuid)}
						onChange={(event) => handleCheckboxChange(row.original.uuid as string, event.currentTarget.checked)}
						color="gray"
						size="sm"
					/>
				) : null
			),
			size: 50,
		}] : []),
		{
			header: "License Pool Name",
			accessorFn: (row: SerialsData) => row.alias || row.name,
		},
		{
			header: "Pool ID",
			accessorKey: "uuid",
		},
		{
			header: () => <div style={{ textAlign: 'center', width: '100%' }}>Pool Balance</div>,
			accessorKey: "balance",
			cell: ({ row }: { row: { original: SerialsData } }) => (
				<div style={{ textAlign: 'center', width: '100%' }}>
					{row.original.balance}
				</div>
			),
		},
		{
			header: "Allocations",
			accessorKey: "assignedTo",
			cell: ({ row }: { row: { original: SerialsData } }) => {
				const assignedTo = row.original.assignedTo;
				const parentPool = row.original.parentPool;

				if (!assignedTo || assignedTo.trim() === '') {
					return <span style={{ color: '#868e96' }}>No allocation</span>;
				}

				const tooltipText = parentPool
					? MSG.HELP.ALLOCATION_FROM_PARENT
					: MSG.HELP.ALLOCATION_TO_ACCOUNT;

				return (
					<Flex gap={4} align="center" mt={0} >
						<span>{assignedTo}</span>
						<Tooltip
							label={tooltipText}
							multiline
							maw={220}
							withArrow
							openDelay={100}
						>
							<div style={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
								<FaInfoCircle size={12} style={{ color: '#868e96' }} />
							</div>
						</Tooltip>
					</Flex>
				);
			},
		},
		{
			header: "Actions",
			cell: ({ row }: { row: { original: SerialsData } }) =>
				row.original.uuid && (
					<Button
						onClick={() => {
							if (row.original.uuid) {
								handleSelectLicense(row.original.uuid);
							}
						}}
						size="xs"
					>
						View
					</Button>
				),
		},
	];

	return (
		<>
			{serialsData.length === 0 && (
				<Alert color="yellow" icon={<FaExclamationCircle />}>
					If there&apos;s no balance displayed, you may have purchased a
					different type of license pool. Please contact our support team if
					you have any questions.
				</Alert>
			)}
			<Table
				data={
					serialsData.length > 0
						? serialsData
						: [{ name: "No license available." }]
				}
				columns={columns}
				isLoading={isSerialsDataLoading}
			/>
		</>
	);
};

export default TableLicenses;
